import { useState, useEffect } from 'react';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { 
  Search, HardDrive, Filter, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, X, ChevronUp, ChevronDown, ChevronsUpDown,
  Eye, Monitor, Power, PowerOff
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { formatDateARG } from '../../../lib/utils';

const VirtDatastores = () => {
  const [loading, setLoading] = useState(true);
  const [datastores, setDatastores] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', ds_type: '', usage_level: '' });
  const [typeOptions, setTypeOptions] = useState([]);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  
  // Active stat filter
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Drill-down modal
  const [selectedDatastore, setSelectedDatastore] = useState(null);
  const [datastoreVMs, setDatastoreVMs] = useState([]);
  const [loadingVMs, setLoadingVMs] = useState(false);

  const fetchDatastores = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.ds_type) params.append('ds_type', filters.ds_type);
      if (filters.usage_level) params.append('usage_level', filters.usage_level);

      const res = await api.get(`/virt/datastores?${params}`);
      setDatastores(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar datastores');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/virt/datastores/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTypeOptions = async () => {
    try {
      const res = await api.get('/virt/datastores/options/types');
      setTypeOptions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch VMs for a datastore (drill-down)
  const fetchDatastoreVMs = async (datastoreId, datastoreName) => {
    setSelectedDatastore({ id: datastoreId, name: datastoreName });
    setLoadingVMs(true);
    try {
      const res = await api.get(`/virt/datastores/${datastoreId}/vms`);
      setDatastoreVMs(res.data.vms);
    } catch (err) {
      toast.error('Error al cargar VMs del datastore');
      setDatastoreVMs([]);
    } finally {
      setLoadingVMs(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTypeOptions();
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchDatastores, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Stat card click handler (thresholds: 85% warning, 90% critical)
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    switch (filterType) {
      case 'critical':
        setFilters({ search: '', ds_type: '', usage_level: 'critical' });
        break;
      case 'warning':
        setFilters({ search: '', ds_type: '', usage_level: 'warning' });
        break;
      case 'healthy':
        setFilters({ search: '', ds_type: '', usage_level: 'healthy' });
        break;
      default:
        setFilters({ search: '', ds_type: '', usage_level: '' });
        break;
    }
  };

  const formatSize = (gb) => {
    if (!gb) return '-';
    const num = Number(gb);
    if (num >= 1024) return `${(num / 1024).toFixed(1)} TB`;
    return `${num.toFixed(0)} GB`;
  };

  // Thresholds: 85% warning, 90% critical
  const getUsageColor = (percent) => {
    const pct = Number(percent) || 0;
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 85) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const clearFilters = () => {
    setFilters({ search: '', ds_type: '', usage_level: '' });
    setActiveStatFilter('all');
  };

  const hasFilters = filters.search || filters.ds_type || filters.usage_level;

  // Calculate usage percentages for stats
  const getUsagePercent = () => {
    if (!stats || !stats.total_capacity_gb) return 0;
    return ((stats.total_used_gb / stats.total_capacity_gb) * 100).toFixed(1);
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th 
      className={`cursor-pointer hover:bg-muted/70 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Datastores</h1>
          <p className="page-subtitle">Almacenamiento de las plataformas de virtualización</p>
        </div>

        {/* Stats - Clickeable Cards (thresholds: 85% warning, 90% critical) */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`}
              onClick={() => handleStatFilter('all')}
              data-testid="stat-total"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{stats.total || 0}</p>
                <p className="stats-card-label">Total</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{formatSize(stats.total_capacity_gb)}</p>
                <p className="stats-card-label">Capacidad</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{formatSize(stats.total_used_gb)}</p>
                <p className="stats-card-label">Usado ({getUsagePercent()}%)</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-green-500">{formatSize(stats.total_free_gb)}</p>
                <p className="stats-card-label">Libre</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'critical' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`}
              onClick={() => handleStatFilter('critical')}
              data-testid="stat-critical"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-red-500">{stats.critical_count || 0}</p>
                <p className="stats-card-label">Críticos (≥90%)</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'warning' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`}
              onClick={() => handleStatFilter('warning')}
              data-testid="stat-warning"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-yellow-500">{stats.warning_count || 0}</p>
                <p className="stats-card-label">Warning (85-90%)</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar datastore..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setActiveStatFilter('');
                  }}
                  className="h-9"
                  data-testid="datastore-search-input"
                />
              </div>
              {typeOptions.length > 0 && (
                <Select 
                  value={filters.ds_type || "todos"} 
                  onValueChange={(v) => {
                    setFilters(prev => ({ ...prev, ds_type: v === "todos" ? "" : v }));
                    setActiveStatFilter('');
                  }}
                >
                  <SelectTrigger className="w-36 h-9" data-testid="datastore-type-filter">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {typeOptions.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="datastores-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">DATASTORE</SortableHeader>
                  <SortableHeader field="type" className="text-left">TIPO</SortableHeader>
                  <SortableHeader field="capacity_gb" className="text-left">CAPACIDAD</SortableHeader>
                  <SortableHeader field="used_pct" className="text-left w-48">USO</SortableHeader>
                  <SortableHeader field="vm_count" className="text-center justify-center">VMs</SortableHeader>
                  <th className="text-center">ACCESIBLE</th>
                  <SortableHeader field="platform_host" className="text-left">PLATAFORMA</SortableHeader>
                  <th className="text-center">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : datastores.length === 0 ? (
                  <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">No hay datastores registrados</td></tr>
                ) : datastores.map(ds => (
                  <tr key={ds.id} className="border-b hover:bg-muted/30" data-testid={`datastore-row-${ds.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{ds.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{ds.type || '-'}</Badge>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-sm">{formatSize(ds.capacity_gb)}</p>
                        <p className="text-xs text-muted-foreground">Libre: {formatSize(ds.free_gb)}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>{Number(ds.used_pct || 0).toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded overflow-hidden">
                          <div 
                            className={`h-full ${getUsageColor(ds.used_pct)}`}
                            style={{ width: `${Math.min(Number(ds.used_pct || 0), 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-center">{ds.vm_count || 0}</td>
                    <td className="p-4 text-center">
                      {ds.is_accessible ? (
                        <CheckCircle className="h-4 w-4 text-green-500 inline" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 inline" />
                      )}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">{ds.platform_host || '-'}</td>
                    <td className="p-4 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => fetchDatastoreVMs(ds.id, ds.name)}
                        title="Ver VMs que usan este datastore"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.pages} ({pagination.total} datastores)
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Drill-down Modal - VMs del Datastore */}
        <Dialog open={!!selectedDatastore} onOpenChange={() => setSelectedDatastore(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                VMs en {selectedDatastore?.name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingVMs ? (
              <div className="text-center p-8 text-muted-foreground">Cargando VMs...</div>
            ) : datastoreVMs.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">No hay VMs usando este datastore</div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">{datastoreVMs.length} VMs consumen este datastore</p>
                  <p className="text-xs text-muted-foreground">* Disco muestra el total de la VM, no el uso específico en este datastore</p>
                </div>
                <table className="table-subtle">
                  <thead className="bg-muted/50">
                    <tr className="border-b">
                      <th 
                        className="cursor-pointer hover:bg-muted/70"
                        onClick={() => setDatastoreVMs(prev => [...prev].sort((a, b) => a.name.localeCompare(b.name)))}
                      >
                        VM
                      </th>
                      <th 
                        className="text-center cursor-pointer hover:bg-muted/70"
                        onClick={() => setDatastoreVMs(prev => [...prev].sort((a, b) => (a.power_state || '').localeCompare(b.power_state || '')))}
                      >
                        ESTADO
                      </th>
                      <th 
                        className="text-center cursor-pointer hover:bg-muted/70"
                        onClick={() => setDatastoreVMs(prev => [...prev].sort((a, b) => (b.vcpu || 0) - (a.vcpu || 0)))}
                      >
                        vCPU
                      </th>
                      <th 
                        className="text-center cursor-pointer hover:bg-muted/70"
                        onClick={() => setDatastoreVMs(prev => [...prev].sort((a, b) => (b.ram_gb || 0) - (a.ram_gb || 0)))}
                      >
                        RAM
                      </th>
                      <th 
                        className="text-right cursor-pointer hover:bg-muted/70"
                        onClick={() => setDatastoreVMs(prev => [...prev].sort((a, b) => (b.disk_total_gb || 0) - (a.disk_total_gb || 0)))}
                      >
                        DISCO TOTAL *
                      </th>
                      <th>SERVIDOR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datastoreVMs.map(vm => (
                      <tr key={vm.id} className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{vm.name}</span>
                            {vm.is_template === 1 && (
                              <Badge variant="secondary" className="text-xs">Template</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {vm.power_state === 'poweredOn' ? (
                            <Power className="h-4 w-4 text-green-500 inline" />
                          ) : (
                            <PowerOff className="h-4 w-4 text-red-500 inline" />
                          )}
                        </td>
                        <td className="p-3 text-center">{vm.vcpu || '-'}</td>
                        <td className="p-3 text-center">{vm.ram_gb ? `${Number(vm.ram_gb).toFixed(0)} GB` : '-'}</td>
                        <td className="p-3 text-right">{formatSize(vm.disk_total_gb)}</td>
                        <td className="p-3 text-sm text-muted-foreground">{vm.server_hostname || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default VirtDatastores;
